import { useMutation } from '@apollo/client'
import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Button,
  ButtonColor,
  Icon,
  IconType,
  NotificationType,
  RadioGroup,
  TextField,
} from '../../../components'
import { Brand, Creator, GetMessagesSort } from '../../../gql/types'
import { useNotification } from '../../../utils/hooks'
import { mapCurrencyToSign } from '../../../utils/mappers'
import { CREATE_TIP_PAYMENT } from '../../../gql/payments'
import { findErrorMethodName } from '../../../utils/helpers'
import { ErrorCode } from '../../../utils/ErrorCode'
import { UpdatePaymentMethodModal } from '../UpdatePaymentMethodModal'
import { GET_MESSAGES_BY_THREAD } from '../../../gql'
import { formatPriceInCents } from '../../../utils/PricingUtil'

type EditTipFormData = {
  tip: string
}

interface IProps {
  brand: Brand
  creator: Creator
  open: boolean
  setOpen: (event: any) => void
  description?: string
  title?: string
  threadId?: number
}

export const TipModal: React.FC<IProps> = ({
  brand,
  creator,
  open,
  setOpen,
  title,
  description,
  threadId,
}) => {
  const { addNotification } = useNotification()
  const cancelButtonRef = useRef(null)
  const [selectedTip, setSelectedTip] = useState('25')

  const [isUpdatePaymentMethodModalOpen, setIsUpdatePaymentMethodModalOpen] =
    useState(false)
  const [createTipPayment, { loading }] = useMutation(CREATE_TIP_PAYMENT, {
    refetchQueries: threadId
      ? [
          {
            query: GET_MESSAGES_BY_THREAD,
            variables: {
              threadId: threadId,
              options: {
                sort: GetMessagesSort.oldest,
                skip: 0,
                limit: 100,
              },
            },
          },
        ]
      : [],
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<EditTipFormData>({
    defaultValues: {
      tip: '25',
    },
  })

  useEffect(() => {
    if (selectedTip) {
      setValue('tip', selectedTip)
    }
  }, [selectedTip])

  const onSubmit = async (data: EditTipFormData) => {
    try {
      await createTipPayment({
        variables: {
          brandId: brand.id,
          creatorId: creator.id,
          amount: parseInt(data.tip),
        },
      })

      addNotification('Your bonus has been sent.', NotificationType.success)
      setOpen(false)
      reset()
    } catch (error) {
      if (findErrorMethodName(error, ErrorCode.couldNotChargePaymentMethod)) {
        setOpen(false)
        setIsUpdatePaymentMethodModalOpen(true)
        return
      }

      addNotification(
        `Something went wrong. Please try again or contact support at hello@useclip.com.`,
        NotificationType.error,
      )
    }
  }

  const tipsOptions = [
    {
      name: formatPriceInCents({
        priceInCents: 1000,
        currency: brand.currency,
      }),
      type: '10',
    },
    {
      name: formatPriceInCents({
        priceInCents: 2500,
        currency: brand.currency,
      }),
      type: '25',
    },
    {
      name: formatPriceInCents({
        priceInCents: 5000,
        currency: brand.currency,
      }),
      type: '50',
    },
    {
      name: formatPriceInCents({
        priceInCents: 10000,
        currency: brand.currency,
      }),
      type: '100',
    },
  ]

  return (
    <>
      <UpdatePaymentMethodModal
        brand={brand}
        open={isUpdatePaymentMethodModalOpen}
        setOpen={setIsUpdatePaymentMethodModalOpen}
      />
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-10'
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <div className='fixed z-10 inset-0 overflow-y-auto'>
            <div className='flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                enterTo='opacity-100 translate-y-0 sm:scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              >
                <Dialog.Panel className='relative bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full'>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4'>
                      <div className='sm:flex sm:items-start'>
                        <div className='mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10'>
                          <div aria-hidden='true'>
                            <Icon
                              type={IconType.money}
                              className='h-5 w-5 text-green-500'
                              aria-hidden='true'
                            />
                          </div>
                        </div>
                        <div className='mt-3 sm:mt-0 sm:ml-4 sm:text-left'>
                          <Dialog.Title
                            as='h3'
                            className='text-lg leading-6 font-medium text-gray-900'
                          >
                            {title ??
                              `Reward ${creator.firstName} for outstanding work`}
                          </Dialog.Title>
                          <div className='my-2'>
                            <p className='text-sm text-gray-500'>
                              {description ??
                                `You've completed a successful collaboration - now show your appreciation with a bonus. Your support acknowledges ${creator.firstName}’s hard work and dedication to making great videos for your brand.`}
                            </p>
                          </div>
                          <div className='grid gap-6 max-h-[400px] overflow-y-auto '>
                            <div className='col-span-3 sm:col-span-2'>
                              <TextField
                                error={errors.tip?.message}
                                // label={description}
                                type='number'
                                placeholder={`${watch('tip')}`}
                                className='pl-7'
                                leftElement={
                                  <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                                    <span className='text-gray-600 sm:text-sm'>
                                      {mapCurrencyToSign(brand.currency)}
                                    </span>
                                  </div>
                                }
                                {...register('tip', {
                                  required: `Please set a value.`,
                                  min: {
                                    value: 1,
                                    message: `Please set value greater than 0.`,
                                  },
                                  validate: {
                                    isInteger: (value) =>
                                      Number.isInteger(Number(value)) ||
                                      'Please set an integer value.',
                                  },
                                })}
                              />
                            </div>
                          </div>
                          <div className='mt-2 grid gap-2'>
                            <Controller
                              name='tip'
                              control={control}
                              render={({ field }) => (
                                <RadioGroup
                                  options={tipsOptions}
                                  field={field}
                                  className='grid-cols-2 lg:grid-cols-4'
                                  error={errors?.tip?.message}
                                  onClick={(value) => setSelectedTip(value)}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
                      <Button
                        colour={ButtonColor.primary}
                        loading={loading}
                        title='Reward'
                        type='submit'
                        className='w-full sm:w-auto'
                      />
                      <button
                        type='button'
                        className='mt-3 mr-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-1 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                        onClick={() => setOpen(false)}
                        ref={cancelButtonRef}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
